/* EvaluationStrip — the collapsible right-hand evaluation rail of a
 * read-side visual: every advisory warning (full sentences, amber) +
 * a one-line icon glossary of what the nodes/edges mean. Pure render —
 * the host visual fetches warnings (useDocWarnings) and matches them
 * to nodes. WarnBadge is the amber on-node marker. */
import React, {
    useState
} from 'react';
import {
    AlertTriangle,
    CheckCircle2,
    ChevronRight,
    Loader2,
    ShieldAlert
} from 'lucide-react';
import {
    parseAdvisory
} from '../advisories';
import './visuals.css';

export interface LegendItem {
    icon: React.ReactNode;
    label: string;
    meaning: string;
}

type Props = {
    /** null = no validate round-trip yet. */
    warnings: string[] | null;
    checking?: boolean;
    legend: LegendItem[];
};

export default function EvaluationStrip({
    warnings,
    checking = false,
    legend
}: Props) {
    const [open, setOpen] = useState(true);
    const list = warnings || [];

    if (!open) {
        return (
            <button
                className="aps-eval-rail"
                title="Open the evaluation strip (advisories + legend)"
                onClick={() => setOpen(true)}
            >
                <ShieldAlert size={14} />
                {list.length > 0 && (
                    <span className="aps-eval-rail-count">{list.length}</span>
                )}
                <span className="aps-eval-rail-label">Evaluation</span>
            </button>
        );
    }

    return (
        <aside className="aps-eval-side">
            <button
                className="aps-eval-head"
                title="Collapse the evaluation strip"
                onClick={() => setOpen(false)}
            >
                <ShieldAlert size={14} />
                <span>Evaluation</span>
                {checking
                    ? <Loader2 size={12} className="aps-spin" />
                    : list.length > 0 && (
                        <span className="aps-eval-rail-count">{list.length}</span>
                    )}
                <ChevronRight size={14} style={{
                    marginLeft: 'auto'
                }} />
            </button>
            <div className="aps-eval-body">
                {list.length === 0 ? (
                    <p className="aps-eval-clean">
                        <CheckCircle2 size={13} />
                        {warnings == null
                            ? 'Evaluating…'
                            : 'No advisories — nothing flagged.'}
                    </p>
                ) : list.map((w, i) => {
                    const p = parseAdvisory(w);
                    return (
                        <div key={i} className="aps-eval-item">
                            {p.code && (
                                <span className="aps-eval-code">{p.code}</span>
                            )}
                            <span>{p.text}</span>
                        </div>
                    );
                })}
                <p className="aps-eval-legend-title">Legend</p>
                {legend.map((it, i) => (
                    <div key={i} className="aps-eval-legend-row">
                        <span className="aps-eval-legend-icon">{it.icon}</span>
                        <span className="aps-eval-legend-label">{it.label}</span>
                        <span className="aps-eval-legend-meaning">{it.meaning}</span>
                    </div>
                ))}
            </div>
        </aside>
    );
}

/** Amber on-node advisory badge — absolutely positioned by the host at
 * the node's top-right corner; hover hands the FULL sentences to the
 * shared viz tooltip. */
export function WarnBadge({
    x,
    y,
    items,
    onEnter,
    onLeave
}: {
    x: number;
    y: number;
    items: string[];
    onEnter: (ev: React.MouseEvent) => void;
    onLeave: () => void;
}) {
    return (
        <span
            className="aps-viz-warnbadge"
            style={{
                left: x,
                top: y
            }}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
        >
            <AlertTriangle size={10} />
            {items.length > 1 ? items.length : null}
        </span>
    );
}
